import { baseApi, defaultQueryFreshness } from "./baseApi";
import type { BlogCategory, BlogPost, BlogPostPayload } from "../../types/blogs";

export const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminReports: builder.query<unknown[], void>({
      query: () => ({ url: "/admin/reports", method: "GET" }),
      providesTags: [{ type: "Admin", id: "reports" }],
      ...defaultQueryFreshness,
    }),
    reviewAdminReport: builder.mutation<unknown, { reportId: string; payload: { action?: string; resolution?: string; notes?: string } }>({
      query: ({ reportId, payload }) => ({ url: `/admin/reports/${reportId}/action`, method: "POST", data: payload }),
      invalidatesTags: [{ type: "Admin", id: "reports" }],
    }),
    getAdminVerifications: builder.query<unknown[], void>({
      query: () => ({ url: "/admin/verifications", method: "GET" }),
      providesTags: [{ type: "Admin", id: "verifications" }],
      ...defaultQueryFreshness,
    }),
    getAdminLogs: builder.query<unknown[], { action?: string; adminId?: string; targetUserId?: string } | void>({
      query: (params) => ({ url: "/admin/logs", method: "GET", params: params || undefined }),
      providesTags: [{ type: "Admin", id: "logs" }],
      ...defaultQueryFreshness,
    }),
    getAdminWaitlist: builder.query<
      { totalCount: number; items: unknown[] },
      { query?: string; favoriteGameOption?: string } | void
    >({
      query: (params) => ({ url: "/admin/waitlist", method: "GET", params: params || undefined }),
      providesTags: [{ type: "Admin", id: "waitlist" }],
      ...defaultQueryFreshness,
    }),
    reviewAdminVerification: builder.mutation<unknown, { verificationId: string; payload: { decision: "approve" | "reject"; rejectionReason?: string | null } }>({
      query: ({ verificationId, payload }) => ({
        url: `/admin/verifications/${verificationId}/review`,
        method: "POST",
        data: payload,
      }),
      invalidatesTags: [{ type: "Admin", id: "verifications" }],
    }),
    getAdminBlogs: builder.query<BlogPost[], void>({
      query: () => ({ url: "/admin/blogs", method: "GET" }),
      providesTags: [{ type: "Admin", id: "blogs" }],
      ...defaultQueryFreshness,
    }),
    getAdminBlogCategories: builder.query<BlogCategory[], void>({
      query: () => ({ url: "/admin/blog-categories", method: "GET" }),
      providesTags: [{ type: "Admin", id: "blog-categories" }],
      ...defaultQueryFreshness,
    }),
    createAdminBlogCategory: builder.mutation<BlogCategory, { name: string }>({
      query: (payload) => ({ url: "/admin/blog-categories", method: "POST", data: payload }),
      invalidatesTags: [{ type: "Admin", id: "blog-categories" }],
    }),
    createAdminBlog: builder.mutation<BlogPost, BlogPostPayload>({
      query: (payload) => ({ url: "/admin/blogs", method: "POST", data: payload }),
      invalidatesTags: [{ type: "Admin", id: "blogs" }],
    }),
    updateAdminBlog: builder.mutation<BlogPost, { postId: string; payload: BlogPostPayload }>({
      query: ({ postId, payload }) => ({ url: `/admin/blogs/${postId}`, method: "PATCH", data: payload }),
      invalidatesTags: [{ type: "Admin", id: "blogs" }],
    }),
    deleteAdminBlog: builder.mutation<{ success: boolean }, { postId: string }>({
      query: ({ postId }) => ({ url: `/admin/blogs/${postId}`, method: "DELETE" }),
      invalidatesTags: [{ type: "Admin", id: "blogs" }],
    }),
  }),
});

export const {
  useGetAdminReportsQuery,
  useReviewAdminReportMutation,
  useGetAdminVerificationsQuery,
  useGetAdminLogsQuery,
  useGetAdminWaitlistQuery,
  useReviewAdminVerificationMutation,
  useGetAdminBlogsQuery,
  useGetAdminBlogCategoriesQuery,
  useCreateAdminBlogCategoryMutation,
  useCreateAdminBlogMutation,
  useUpdateAdminBlogMutation,
  useDeleteAdminBlogMutation,
} = adminApi;
